import type { BetType } from '@/lib/golfEngine';
import { Button } from '@/components/ui/button';
import { X, AlertTriangle } from 'lucide-react';
import { DT } from '@/lib/darkTheme';

export type PendingPress = {
  type: 'new';
  betType: BetType;
  side: 'top' | 'bottom';
  count: number;
  holeOverride?: number;
} | {
  type: 'existing';
  pressId: string;
  side: 'top' | 'bottom';
  label: string;
}

interface ConfirmPressModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  pending: PendingPress | null;
  isSyncing?: boolean;
}

function getPressDescription(pending: PendingPress | null): string {
  if (!pending) return '';
  if (pending.type === 'existing') {
    return `Press on ${pending.label}`;
  }
  const legName = pending.betType === 'Front' ? 'Front Nine' : pending.betType === 'Back' ? 'Back Nine' : 'Overall';
  if (pending.count > 1) return `${pending.count}x Press on the ${legName}`;
  return `Press on the ${legName}`;
}

export function ConfirmPressModal({ isOpen, onClose, onConfirm, pending, isSyncing }: ConfirmPressModalProps) {
  if (!isOpen || !pending) return null;

  const description = getPressDescription(pending);

  return (
    <>
      <div
        className="fixed inset-0 z-[60] bg-black/50"
        onClick={onClose}
        data-testid="confirm-press-backdrop"
      />
      <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 pointer-events-none">
        <div
          className="pointer-events-auto w-[90vw] max-w-sm rounded-2xl shadow-2xl overflow-hidden relative"
          style={{ background: DT.surface, border: `1px solid ${DT.divider}` }}
          onClick={e => e.stopPropagation()}
          data-testid="confirm-press-modal"
        >
          <div className="px-5 pt-5 pb-3">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: `${DT.interactive}22` }}>
                <AlertTriangle className="w-5 h-5" style={{ color: DT.interactive }} />
              </div>
              <div>
                <h3 style={{ color: DT.primaryText, fontWeight: 800, fontSize: '18px' }} data-testid="confirm-press-title">
                  Confirm Press
                </h3>
              </div>
            </div>
            <p style={{ color: DT.primaryText, fontWeight: 600, fontSize: '16px' }} className="mb-1" data-testid="confirm-press-description">
              {description}
            </p>
            <p style={{ fontSize: '14px', color: DT.primaryText, opacity: 0.5 }}>
              This will create a new money bet. Are you sure?
            </p>
          </div>
          <div className="flex gap-3 px-5 pb-5 pt-2">
            <Button
              variant="outline"
              className="flex-1 min-h-[44px] font-bold text-base"
              style={{ background: DT.darkBg, color: DT.primaryText, borderColor: DT.divider }}
              onClick={onClose}
              disabled={isSyncing}
              data-testid="confirm-press-cancel"
            >
              Cancel
            </Button>
            <Button
              className="flex-1 min-h-[44px] font-bold text-base"
              style={{ background: DT.interactive, color: DT.darkBg }}
              onClick={onConfirm}
              disabled={isSyncing}
              data-testid="confirm-press-confirm"
            >
              {isSyncing ? 'Pressing...' : 'Confirm Press'}
            </Button>
          </div>
          <button
            onClick={onClose}
            className="absolute top-3 right-3 min-w-[32px] min-h-[32px] flex items-center justify-center rounded-full transition-colors"
            style={{ color: DT.primaryText, opacity: 0.5 }}
            data-testid="confirm-press-close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>
    </>
  );
}
